import { FlexContainer } from '@/ui/styled-ui';
import {
  ChartEmptyStateAWTIcon,
  ChartEmptyStateGenericIcon,
  ChartEmptyStatePhishingIcon,
  ChartEmptyStateSCIcon,
} from '@/utils/helpers';
import React from 'react';
import styled from 'styled-components';

export enum ChartEmptyStateType {
  AWT = 'AWT',
  PHISHING = 'PHISHING',
  SC = 'SC',
  GENERIC = 'GENERIC',
}

const EmptyStateWrapper = styled.div<{
  $noBorder?: boolean;
  $minHeight?: string | number;
  $noMargin?: boolean;
  $transparent?: boolean;
}>`
  width: 100%;
  flex: 1;
  min-height: ${({ $minHeight }) =>
    $minHeight != null
      ? typeof $minHeight === 'number'
        ? `${$minHeight}px`
        : $minHeight
      : 'unset'};
  border: ${({ $noBorder }) =>
    $noBorder ? 'none' : '1px solid var(--gray-medium)'};
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  background: ${({ $transparent }) =>
    $transparent ? 'transparent' : 'var(--white-main)'};
  margin-top: ${({ $noMargin }) => ($noMargin ? 0 : 'var(--default-padding)')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h3`
  font-size: var(--medium-font-size);
  line-height: var(--medium-line-height);
  font-weight: 700;
  color: var(--gray-text);
  margin: 0;
`;

const Description = styled.p`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 400;
  color: var(--gray-text);
  margin: 0;
  text-align: center;
`;

const typeConfig: Record<ChartEmptyStateType, { icon: React.ReactNode }> = {
  [ChartEmptyStateType.AWT]: {
    icon: <ChartEmptyStateAWTIcon />,
  },
  [ChartEmptyStateType.PHISHING]: {
    icon: <ChartEmptyStatePhishingIcon />,
  },
  [ChartEmptyStateType.SC]: {
    icon: <ChartEmptyStateSCIcon />,
  },
  [ChartEmptyStateType.GENERIC]: {
    icon: <ChartEmptyStateGenericIcon />,
  },
};

export type ChartEmptyStateProps = {
  title: string;
  description: string;
  type: ChartEmptyStateType;
  noBorder?: boolean;
  minHeight?: string | number;
  noMargin?: boolean;
  transparent?: boolean;
};

export const ChartEmptyState: React.FC<ChartEmptyStateProps> = ({
  title,
  description,
  type,
  noBorder = false,
  minHeight,
  noMargin = false,
  transparent = false,
}) => {
  const { icon } = typeConfig[type];

  return (
    <EmptyStateWrapper
      $noBorder={noBorder}
      $minHeight={minHeight}
      $noMargin={noMargin}
      $transparent={transparent}
    >
      <FlexContainer
        height="100%"
        direction="column"
        justify="center"
        align="center"
        gap={5}
      >
        <IconWrapper>{icon}</IconWrapper>
        <Title>{title}</Title>
        <Description>{description}</Description>
      </FlexContainer>
    </EmptyStateWrapper>
  );
};
